import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdChatBubbleOutline, MdDeleteOutline, MdFavorite, MdFavoriteBorder, MdReply, MdSend } from 'react-icons/md';
import { useAuthUser } from '../../hooks/useAuthUser';
import { UserContext } from '../../contexts/UserContext';
import { requestWithApiFallback } from '../../utils/apiRequest';
import formatDisplayName from '../../utils/formatDisplayName';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';
import styles from './PostDetail.module.css';

function buildCommentTree(comments) {
	const map = {};
	const roots = [];
	for (const c of comments) {
		map[String(c._id)] = { ...c, children: [] };
	}
	for (const c of comments) {
		const node = map[String(c._id)];
		if (c.parentCommentID && map[c.parentCommentID]) {
			map[c.parentCommentID].children.push(node);
		} else {
			roots.push(node);
		}
	}
	return roots;
}

const PostDetail = () => {
	const { postID } = useParams();
	const navigate = useNavigate();
	const { user } = useAuthUser();
	const { dbUser } = useContext(UserContext);
	const API_URL = process.env.REACT_APP_API_URL;
	const requestApi = useCallback((factory) => requestWithApiFallback(API_URL, factory), [API_URL]);

	const [post, setPost] = useState(null);
	const [loading, setLoading] = useState(true);
	const [reply, setReply] = useState('');
	const [replyToComment, setReplyToComment] = useState(null);
	const [savingReply, setSavingReply] = useState(false);
	const [deletingPost, setDeletingPost] = useState(false);
	const [deletingCommentID, setDeletingCommentID] = useState('');

	const fetchPost = useCallback(async () => {
		try {
			const response = await requestApi((base) => axios.get(`${base}/feed/post/${postID}`));
			setPost(response.data.post || null);
		} catch (error) {
			console.error('Post detail fetch error:', error.message);
			setPost(null);
		} finally {
			setLoading(false);
		}
	}, [postID, requestApi]);

	useEffect(() => {
		setLoading(true);
		fetchPost();
	}, [fetchPost]);

	const toggleLike = async () => {
		if (!user?.id || !post?._id) return;
		try {
			const response = await requestApi((base) => axios.post(`${base}/feed/${post._id}/like`, {
				userID: user.id,
				username: dbUser?.username || user.username || user.firstName || 'User'
			}));
			setPost(response.data);
		} catch (error) {
			console.error('Like error:', error.message);
		}
	};

	const submitReply = async () => {
		const text = reply.trim();
		if (!text || !post?._id || !user?.id || savingReply) return;
		setSavingReply(true);
		try {
			const response = await requestApi((base) => axios.post(`${base}/feed/${post._id}/comment`, {
				userID: user.id,
				username: dbUser?.username || user.username || user.firstName || 'User',
				profileImageLink: user.profileImageUrl,
				text,
				parentCommentID: replyToComment?._id || null
			}));
			setPost(response.data);
			setReply('');
			setReplyToComment(null);
		} catch (error) {
			console.error('Reply error:', error.message);
		} finally {
			setSavingReply(false);
		}
	};

	const handleReplyTo = (comment) => {
		setReplyToComment(comment);
		setReply('');
	};

	const deletePost = async () => {
		if (!post?._id || !user?.id || deletingPost) return;
		setDeletingPost(true);
		try {
			await requestApi((base) => axios.delete(`${base}/feed/${post._id}`, {
				data: { userID: user.id }
			}));
			navigate('/feed');
		} catch (error) {
			console.error('Delete post error:', error.message);
		} finally {
			setDeletingPost(false);
		}
	};

	const deleteComment = async (comment) => {
		if (!post?._id || !user?.id || !comment?._id || deletingCommentID) return;
		setDeletingCommentID(String(comment._id));
		try {
			const response = await requestApi((base) => axios.delete(
				`${base}/feed/${post._id}/comment/${comment._id}`,
				{ data: { userID: user.id } }
			));
			setPost(response.data);
			if (replyToComment?._id === comment._id) {
				setReplyToComment(null);
			}
		} catch (error) {
			console.error('Delete comment error:', error.message);
		} finally {
			setDeletingCommentID('');
		}
	};

	const formatTimestamp = (value) => {
		try { return new Date(value).toLocaleString(); }
		catch { return ''; }
	};

	if (loading) {
		return (
			<div className={styles.PostDetailPage}>
				<p className={styles.MutedText}>Loading post...</p>
			</div>
		);
	}

	if (!post) {
		return (
			<div className={styles.PostDetailPage}>
				<button type='button' className={styles.BackBtn} onClick={() => navigate('/feed')}>
					<MdArrowBack />
					<span>Back to feed</span>
				</button>
				<p className={styles.MutedText}>Post not found.</p>
			</div>
		);
	}

	const isLiked = post.likes?.some((entry) => entry.userID === user?.id);
	const isOwner = user?.id === post.userID;
	const commentTree = buildCommentTree(post.comments || []);

	return (
		<div className={styles.PostDetailPage}>
			<button type='button' className={styles.BackBtn} onClick={() => navigate('/feed')}>
				<MdArrowBack />
				<span>Back to feed</span>
			</button>

			<article className={styles.PostCard}>
				<div className={styles.PostHeader}>
					<Link to={`/profile/${post.userID}`} className={styles.PostAuthor}>
						<img src={post.profileImageLink} alt='' className={styles.Avatar} />
						<div className={styles.AuthorInfo}>
							<span className={styles.Username}>
								{formatDisplayName(post.username)}
								<VerifiedBadge userPlan={post.userPlan} totalXP={post.totalXP} size='small' />
							</span>
							<span className={styles.Time}>{formatTimestamp(post.createdAt)}</span>
						</div>
					</Link>
					{isOwner && (
						<button type='button' className={styles.DeleteBtn} onClick={deletePost} disabled={deletingPost}>
							<MdDeleteOutline />
							<span>{deletingPost ? 'Deleting...' : 'Delete'}</span>
						</button>
					)}
				</div>

				<p className={styles.Content}>{post.content}</p>

				{post.tags?.length > 0 && (
					<div className={styles.TagRow}>
						{post.tags.map((tag, i) => (
							<Link
								key={i}
								className={styles.TagChip}
								to={tag.tagType === 'user' ? `/profile/${tag.refID}` : `/stryd/${tag.refID}/${tag.name}`}
							>
								{tag.tagType === 'user' ? '@' : '#'}{tag.name}
							</Link>
						))}
					</div>
				)}

				<div className={styles.Actions}>
					<button type='button' className={`${styles.ActionBtn} ${isLiked ? styles.Liked : ''}`} onClick={toggleLike}>
						{isLiked ? <MdFavorite /> : <MdFavoriteBorder />}
						<span>{post.likes?.length || 0} {(post.likes?.length || 0) === 1 ? 'like' : 'likes'}</span>
					</button>
					<div className={styles.ActionBtn}>
						<MdChatBubbleOutline />
						<span>{post.comments?.length || 0} {(post.comments?.length || 0) === 1 ? 'reply' : 'replies'}</span>
					</div>
				</div>
			</article>

			<section className={styles.RepliesSection}>
				<h3 className={styles.RepliesTitle}>Replies</h3>

				{commentTree.length > 0 ? (
					<div className={styles.RepliesList}>
						{commentTree.map((comment) => (
							<CommentThread
								key={comment._id}
								comment={comment}
								depth={0}
								onReply={handleReplyTo}
								onDelete={deleteComment}
								deletingCommentID={deletingCommentID}
								user={user}
								styles={styles}
								formatTimestamp={formatTimestamp}
							/>
						))}
					</div>
				) : (
					<p className={styles.MutedText}>No replies yet. Be the first to reply.</p>
				)}

				{user ? (
					<div className={styles.ReplyComposer}>
						{replyToComment && (
							<div className={styles.ReplyContext}>
								<span>Replying to <strong>{formatDisplayName(replyToComment.username)}</strong></span>
								<button type='button' className={styles.CancelReply} onClick={() => setReplyToComment(null)}>Cancel</button>
							</div>
						)}
						<div className={styles.ReplyInputRow}>
							<input
								className={styles.ReplyInput}
								placeholder={replyToComment ? `Reply to ${formatDisplayName(replyToComment.username)}...` : 'Write a reply...'}
								value={reply}
								onChange={(e) => setReply(e.target.value)}
								onKeyDown={(e) => e.key === 'Enter' && submitReply()}
							/>
							<button
								type='button'
								className={styles.SendBtn}
								onClick={submitReply}
								disabled={!reply.trim() || savingReply}
							>
								<MdSend />
							</button>
						</div>
					</div>
				) : (
					<p className={styles.MutedText}>Sign in to join the conversation.</p>
				)}
			</section>
		</div>
	);
};

const CommentThread = ({ comment, depth, onReply, onDelete, deletingCommentID, user, styles, formatTimestamp }) => {
	const maxIndent = 3;
	const indent = Math.min(depth, maxIndent);
	const canDelete = user?.id && user.id === comment.userID;
	const isDeleting = deletingCommentID === String(comment._id);

	return (
		<div className={styles.ThreadWrapper} style={{ marginLeft: indent > 0 ? `${indent * 20}px` : undefined }}>
			<div className={styles.ReplyCard}>
				<div className={styles.ReplyTop}>
					<Link to={`/profile/${comment.userID}`} className={styles.ReplyUser}>
						{comment.profileImageLink && (
							<img src={comment.profileImageLink} alt='' className={styles.ReplyAvatar} />
						)}
						<span>
							{formatDisplayName(comment.username)}
							<VerifiedBadge userPlan={comment.userPlan} totalXP={comment.totalXP} size='small' />
						</span>
					</Link>
					<span className={styles.Time}>{formatTimestamp(comment.createdAt)}</span>
				</div>
				<p className={styles.ReplyText}>{comment.text}</p>
				{user && (
					<div className={styles.ReplyActions}>
						<button type='button' className={styles.ReplyToBtn} onClick={() => onReply(comment)}>
							<MdReply />
							<span>Reply</span>
						</button>
						{canDelete && (
							<button
								type='button'
								className={styles.CommentDeleteBtn}
								onClick={() => onDelete(comment)}
								disabled={isDeleting}
							>
								<MdDeleteOutline />
								<span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
							</button>
						)}
					</div>
				)}
			</div>
			{comment.children?.length > 0 && comment.children.map((child) => (
				<CommentThread
					key={child._id}
					comment={child}
					depth={depth + 1}
					onReply={onReply}
					onDelete={onDelete}
					deletingCommentID={deletingCommentID}
					user={user}
					styles={styles}
					formatTimestamp={formatTimestamp}
				/>
			))}
		</div>
	);
};

export default PostDetail;
